'use client';

import { useEffect, useRef } from 'react';

const ADSENSE_CLIENT_ID = 'ca-pub-2214184889025947';

/**
 * Vertical skyscraper ad, sticky in the desktop right-hand gutter.
 *
 * For pages whose content is a single centered column (max-w-4xl ≈ 896px) with
 * no sidebar — e.g. glossary term pages. Shown only at ≥1280px (`xl`), where the
 * gutter is wide enough for a 160px unit without overlapping content; hidden
 * below that. `position: fixed` keeps it pinned while scrolling.
 *
 * SETUP: paste the numeric id of a vertical AdSense unit into VERTICAL_SLOT
 * (shared across every skyscraper/sidebar unit on the site — one slot, reused).
 * Until a real numeric slot is set the component renders nothing AND pushes
 * nothing to adsbygoogle, so it can never mis-fill another unit. Requires the
 * adsbygoogle.js loader (<AdSenseLoader />) on the same page.
 */
const VERTICAL_SLOT = '1406935641';

/**
 * DISABLED 2026-07-27 in favour of AdSense Auto Ads "side rail" ads, which
 * stick to the same screen edges as this unit and would compete with it for
 * the identical gutter at the identical breakpoint. Leaving both on risks
 * Google suppressing the side rails (no gain) or visibly doubling up.
 *
 * Rationale: Auto ads ran $4.16 impression RPM at 90.67% viewability over the
 * 7 days to 2026-07-26, versus $0.50-0.83 at ~77-81% for the manual
 * skyscrapers — the most viewable but among the worst earning manual units, so
 * the best candidate to hand over.
 *
 * Flip back to `true` to restore; the slot id above is intentionally kept so
 * re-enabling is a one-line change. Gating `isConfigured` (rather than
 * removing the JSX) preserves the component's invariant that it renders
 * nothing AND pushes nothing — a bare push({}) with no <ins> would be consumed
 * by some other unit on the page.
 */
const SKYSCRAPER_ENABLED = false;

export default function SkyscraperAd() {
  const pushed = useRef(false);
  const isConfigured = SKYSCRAPER_ENABLED && /^\d+$/.test(VERTICAL_SLOT);

  useEffect(() => {
    if (!isConfigured || pushed.current) return;
    pushed.current = true;
    try {
      const w = window as unknown as { adsbygoogle: unknown[] };
      (w.adsbygoogle = w.adsbygoogle || []).push({});
    } catch {
      // Loader not ready yet or slot already filled — safe to ignore.
    }
  }, [isConfigured]);

  if (!isConfigured) return null;

  return (
    <aside
      aria-label="Advertisement"
      className="fixed right-4 top-32 z-30 hidden w-[160px] xl:block"
    >
      <div className="mb-1 select-none text-center text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500">
        Advertisement
      </div>
      <ins
        className="adsbygoogle"
        style={{ display: 'inline-block', width: 160, height: 600 }}
        data-ad-client={ADSENSE_CLIENT_ID}
        data-ad-slot={VERTICAL_SLOT}
      />
    </aside>
  );
}
